import { escapeHtml, fromNow, stamp } from './format'
import { UNASSIGNED_TEXT, hostTypeHtml } from './columns'
import type { Column } from './columns'
import { assetUrl } from '../ui/assetUrl'
import { assignCellHtml, unassignedRowClass } from './UnassignedFirst'
import { autoBadgeHtml } from './ownerMarks'

/**
 * OverviewDevicesModel din public/js/overview.js, plus OdHostType,
 * cu acelasi HTML pe celula. device_icon, actions, power_status,
 * agentVersionIcon vin gata de la server.
 */

export interface ManagedRow {
  ID: number
  DT_RowId: string
  Tip: number
  device_type: string
  device_icon: string
  actions: string
  Denumire: string
  ownerId: number
  Prenume: string | null
  Nume: string | null
  owner_name?: string | null
  UserLogat: string | null
  office_name: string | null
  DeviceGroup: string | null
  DataAdaugare: string | null
  sAgentVersion: string | null
  agentVersionIcon: string | null
  device_status: string | null
  power_status: string | null
  DataAccesareDM: string | null
  inventoree_module: number
  softsee_module: number
  proactivitee_module: number
  saveenergee_module: number
  printeeng_module: number
  securitee_module: number
  protectee_module: number
  productivitee_module: number
  easeedesk_module: number
  sLastIPIntern: string | null
  last_seen: string | null
  checking: number
  host_type_label?: string
  host_device_name?: string | null
  __checked?: boolean
  [key: string]: unknown
}

const DASH = '<i class="fas fa-minus font-10"></i>'

function moduleOn(title: string): string {
  return `<i data-toggle="tooltip" data-placement="top" title="${title} module is ENABLED" class="far fa-check-circle font-18 color-green2">`
}

function moduleOff(title: string): string {
  return `<i data-toggle="tooltip" data-placement="top" title="${title} module is DISABLED" class="far fa-dot-circle font-18 color-red">`
}

function moduleTitle(src: string, name: string): string {
  return `<img data-toggle="tooltip" data-placement="top" src="${assetUrl(`images/m-icons/${src}`)}" title="${name}"/><span style="display:none;">${name}</span>`
}

export function managedColumns(now: () => Date = () => new Date(), canAssign = false): Column<ManagedRow>[] {
  return [
    {
      index: 0,
      title: '',
      titleHtml: '<span class="align-center"><input type="checkbox" class="allDevices"/></span>',
      orderable: false,
      visible: true,
      width: 50,
      html: (r) => `<span class="align-center"><input value="${r.ID}" type="checkbox" class="oneDevice"${r.__checked ? ' checked' : ''}/></span>`,
      /* Semnul de pe randul unui aparat nealocat - pe coloana bifei, care nu
         se poate ascunde din meniul "Columns". */
      rowClass: unassignedRowClass,
    },
    {
      index: 1,
      title: '',
      orderable: false,
      visible: true,
      width: 50,
      html: (r) => `<center class="show_details color-dark-grey pointer font-16" data-id="${r.ID}"><i class="fas fa-plus-circle"></i></center>`,
    },
    {
      index: 2,
      title: 'Type',
      name: 'Tip',
      orderable: true,
      visible: true,
      html: (r) =>
        `<center style="cursor:default" data-toggle="tooltip" data-placement="top" title="${escapeHtml(r.device_type)}">${r.device_icon}</center>`,
    },
    {
      index: 3,
      title: 'Device name',
      name: 'Denumire',
      orderable: true,
      visible: true,
      html: (r) => `<p>${escapeHtml(r.Denumire)}</p>`,
    },
    {
      index: 4,
      title: 'Owned by',
      name: 'owner_name',
      orderable: true,
      visible: true,
      /* Aici scria `UNASSIGNED_TEXT` - vezi comentariul din `columns.ts`
         (Roxana, 24 septembrie 2026). Acum e butonul Assign, si textul ramane
         numai cand omul n-are dreptul de alocare. */
      html: (r) =>
        Number(r.ownerId) > 0
          ? `<span class="color-light-grey"><i class="fa fa-user"></i> ${escapeHtml(r.Prenume)} ${escapeHtml(r.Nume)}</span>` +
            autoBadgeHtml(r.owner_auto)
          : canAssign
            ? assignCellHtml(Number(r.ID))
            : UNASSIGNED_TEXT,
    },
    {
      index: 5,
      title: 'Office',
      name: 'office_name',
      orderable: true,
      visible: true,
      html: (r) => (r.office_name ? escapeHtml(r.office_name) : DASH),
    },
    {
      index: 6,
      title: 'Group',
      name: 'DeviceGroup',
      orderable: true,
      visible: true,
      html: (r) => (r.DeviceGroup ? escapeHtml(r.DeviceGroup) : DASH),
    },
    {
      index: 7,
      title: 'Last user logged on',
      name: 'UserLogat',
      orderable: true,
      visible: true,
      html: (r) => (r.UserLogat ? escapeHtml(r.UserLogat) : DASH),
    },
    {
      index: 8,
      title: 'Registered',
      name: 'DataAdaugare',
      orderable: true,
      visible: true,
      html: (r) => {
        const when = stamp(r.DataAdaugare)
        return when ? fromNow(when, now()) : DASH
      },
    },
    {
      index: 9,
      title: 'Agent',
      name: 'sAgentVersion',
      orderable: true,
      visible: true,
      html: (r) => (r.agentVersionIcon ? `<center>${r.agentVersionIcon}</center>` : DASH),
    },
    {
      index: 10,
      title: 'Status',
      name: 'device_status',
      orderable: true,
      visible: true,
      html: (r) => (r.power_status ? String(r.power_status) : DASH),
    },
    {
      index: 11,
      title: 'Last Activity',
      name: 'DataAccesareDM',
      orderable: true,
      visible: true,
      html: (r) => {
        const when = stamp(r.DataAccesareDM)
        return when ? fromNow(when, now()) : DASH
      },
    },
    {
      index: 12,
      title: 'Inventoree',
      titleHtml: moduleTitle('min_icon_inventoree.png', 'Inventoree'),
      name: 'inventoree_module',
      orderable: true,
      visible: true,
      html: (r) => (Number(r.inventoree_module) > 0 ? moduleOn('Inventoree') : moduleOff('Inventoree')),
    },
    {
      index: 13,
      title: 'Softsee',
      titleHtml: moduleTitle('min_icon_softsee.png', 'Softsee'),
      name: 'softsee_module',
      orderable: true,
      visible: true,
      html: (r) => (Number(r.softsee_module) > 0 ? moduleOn('Softsee') : moduleOff('Softsee')),
    },
    {
      index: 14,
      title: 'Proactivitee',
      titleHtml: moduleTitle('min_icon_opteemum.png', 'Proactivitee'),
      name: 'proactivitee_module',
      orderable: true,
      visible: true,
      html: (r) => {
        if (Number(r.proactivitee_module) > 0) {
          if (Number(r.proactivitee_module) === 4) {
            return '<span class="label label-success bg-green2"><i data-original-title="Proactivitee module is ENABLED" data-toggle="tooltip" data-placement="top" title="" class="fa fa-check font-12"></i> E</span>'
          }
          return moduleOn('Proactivitee')
        }
        return moduleOff('Proactivitee')
      },
    },
    {
      index: 15,
      title: 'SaveEnergee',
      titleHtml: moduleTitle('min_icon_savenergee.png', 'SaveEnergee'),
      name: 'saveenergee_module',
      orderable: true,
      visible: true,
      html: (r) => (Number(r.saveenergee_module) > 0 ? moduleOn('Saveenergee') : moduleOff('Saveenergee')),
    },
    {
      index: 16,
      title: 'Printeeng',
      titleHtml: moduleTitle('min_icon_printeeng.png', 'Printeeng'),
      name: 'printeeng_module',
      orderable: true,
      visible: true,
      html: (r) => (Number(r.printeeng_module) > 0 ? moduleOn('Printeeng') : moduleOff('Printeeng')),
    },
    {
      index: 17,
      title: 'Securitee',
      titleHtml: moduleTitle('min_icon_securitee.png', 'Securitee'),
      name: 'securitee_module',
      orderable: true,
      visible: true,
      html: (r) => (Number(r.securitee_module) > 0 ? moduleOn('Securitee') : moduleOff('Securitee')),
    },
    {
      index: 18,
      title: 'Protectee',
      titleHtml: moduleTitle('min_icon_protectee.png', 'Protectee'),
      name: 'protectee_module',
      orderable: true,
      visible: true,
      html: (r) => (Number(r.protectee_module) > 0 ? moduleOn('Protectee') : moduleOff('Protectee')),
    },
    {
      index: 19,
      title: 'Productivitee',
      titleHtml: moduleTitle('min_icon_productivitee.png', 'Productivitee'),
      name: 'productivitee_module',
      orderable: true,
      visible: true,
      html: (r) => (Number(r.productivitee_module) > 0 ? moduleOn('Productivitee') : moduleOff('Productivitee')),
    },
    {
      index: 20,
      title: 'Easeedesk',
      titleHtml: moduleTitle('min_icon_support.png', 'Easeedesk'),
      name: 'easeedesk_module',
      orderable: true,
      visible: true,
      html: (r) => (Number(r.easeedesk_module) > 0 ? moduleOn('Easeedesk') : moduleOff('Easeedesk')),
    },
    {
      index: 21,
      title: 'Local IP',
      name: 'sLastIPIntern',
      orderable: true,
      visible: true,
      html: (r) => (r.sLastIPIntern ? escapeHtml(r.sLastIPIntern) : DASH),
    },
    {
      index: 22,
      /* Coloana citeste `it_discovered_devices.last_saw`: cand a vazut-o
         DISCOVERY-ul, nu agentul. "Last Seen" se confunda cu "Last Activity"
         (DataAccesareDM), de aici numele dupa sursa (cec-master c2dafbe61). */
      title: 'Seen by discovery',
      name: 'last_seen',
      orderable: true,
      visible: true,
      html: (r) => {
        if (!r.last_seen) return DASH
        if (Number(r.checking) === 1) return '<label class="label label-danger">Checking...</label> '
        let mark = ''
        if (Number(r.checking) === 2) mark = '<i class="fas fa-check-circle font-18 color-green2"></i> '
        if (Number(r.checking) === 3) mark = '<i class="fas fa-exclamation-circle font-18 color-red"></i> '
        const when = stamp(r.last_seen)
        return mark + (when ? fromNow(when, now()) : DASH)
      },
    },
    {
      index: 23,
      title: 'Actions',
      orderable: false,
      visible: true,
      width: 100,
      html: (r) => (r.actions ? `<center>${r.actions}</center>` : `<center>${DASH}</center>`),
    },
    {
      index: 24,
      title: 'Host Type',
      orderable: false,
      visible: false,
      html: (r) => hostTypeHtml(r),
    },
  ]
}
